import {
  IConvertTemplateRequestPayload,
  OnsiteTemplatePreviewPayload,
  OnsiteTemplatePreviewQueryResponse,
  OnsiteTemplatePreviewResponse,
} from './../../../entities/templates';
import { get, post, put, RestApiError } from 'core/network/RestApi';
import {
  IOffsiteTemplatePreviewPayload,
  IOffsiteTemplatePreviewResponse,
  IUpdateTemplatePayload,
  IRestoreTemplateVersionRequestPayload,
  IRestoreTemplateVersionResponse,
} from 'sections/design/template-management/entities/templates';
import { TemplateApiEndpoints } from 'sections/design/template-management/constants/api-endpoints';
import { TFunction } from 'i18next';
import { generatePath } from 'react-router';
import { ITemplateHistoryItem } from 'entities/template/template';
import { OnsiteRandomiseSnapshotMapper } from './OnsiteRandomiseSnapshotMapper';
import { Snapshot } from 'entities/compilationContext/compilation-context';

export interface OffsitePreviewTemplateRequest {
  payload: IOffsiteTemplatePreviewPayload;
  t: TFunction;
}

export const offsitePreviewTemplate = async ({
  payload,
  t,
}: OffsitePreviewTemplateRequest): Promise<IOffsiteTemplatePreviewResponse> => {
  // Hide email hash and replace with fake email address looking value for template previews
  // Don't replace when it seems to be a real email address
  let modifiedPayload = payload;
  payload.featureType = payload.featureType === 'sendMyBasket' ? 'send-my-basket' : payload.featureType;
  if (payload.compilationContext.email?.value && !(payload.compilationContext.email.value as string).includes('@')) {
    modifiedPayload = {
      ...payload,
      compilationContext: {
        ...payload.compilationContext,
        email: {
          ...payload.compilationContext.email,
          value: 'charles.windsor@royalsc.com',
        },
        customer: {
          ...payload.compilationContext.customer,
          firstName: 'Charles',
          lastName: 'Windsor',
          salutation: 'Mr',
        },
        phone: {
          ...payload.compilationContext.phone,
          mobile: '07123456789',
          landline: '01234567890',
        },
      },
    } as IOffsiteTemplatePreviewPayload;
  }
  try {
    const result = await post<IOffsiteTemplatePreviewResponse, IOffsiteTemplatePreviewPayload>(
      generatePath(TemplateApiEndpoints.OFFSITE_PREVIEW),
      modifiedPayload,
    );
    if (result?.data === null) {
      throw Error(t('designGallery.templateEditor.queries.preview.error'), {
        cause: 'Unknown error',
      } as ErrorOptions);
    }
    return result.data;
  } catch (e) {
    const errorDetails = (e as RestApiError).response?.data?.message ?? '';
    throw Error(
      t('designGallery.templateEditor.queries.preview.error'),
      errorDetails ? ({ cause: errorDetails } as ErrorOptions) : null,
    );
  }
};

export const onsitePreviewTemplate = async ({
  payload,
  t,
}: {
  payload: OnsiteTemplatePreviewPayload;
  t: TFunction;
}): Promise<OnsiteTemplatePreviewQueryResponse> => {
  try {
    const result = await post<OnsiteTemplatePreviewResponse, OnsiteTemplatePreviewPayload>(
      generatePath(TemplateApiEndpoints.ONSITE_PREVIEW),
      payload,
    );

    if (result?.data === null) {
      throw Error(t('designGallery.templateEditor.queries.preview.error'), {
        cause: 'Unknown error',
      } as ErrorOptions);
    }

    return {
      compiled: result.data.template,
      compilationContext: result.data.compilationContext,
    };
  } catch (e) {
    const errorDetails = (e as RestApiError).response?.data?.message ?? '';

    throw Error(
      t('designGallery.templateEditor.queries.preview.error'),
      errorDetails ? ({ cause: errorDetails } as ErrorOptions) : null,
    );
  }
};

interface RandomSnapshotsRequest {
  siteId: string;
  channel: 'onsite' | 'offsite';
  t: TFunction;
}

export const getRandomSnapshotSelection = async ({
  siteId,
  channel,
  t,
}: RandomSnapshotsRequest): Promise<Snapshot<any>[]> => {
  const path = TemplateApiEndpoints.GET_OFFSITE_SNAPSHOTS;

  try {
    const result = await get<Snapshot[]>(generatePath(path, { siteId }));
    if (result?.data === null) {
      return [];
    }

    if (channel === 'onsite') {
      const t = new OnsiteRandomiseSnapshotMapper().map(result.data);
      return t;
    }

    return result.data;
  } catch (e) {
    throw Error(t('designGallery.templateEditor.queries.randomSnapshots.error'));
  }
};

interface ImportSnapshotRequest {
  siteId: string;
  snapshotId: string;
  t: TFunction;
}
export const importSnapshot = async ({ siteId, snapshotId, t }: ImportSnapshotRequest): Promise<any> => {
  try {
    const result = await get<any>(generatePath(TemplateApiEndpoints.GET_SNAPSHOT, { siteId, snapshotId }));
    return result.data;
  } catch (e) {
    throw Error(t('importSessionModal.queries.importSnapshot.error'));
  }
};

export interface SaveTemplateRequest {
  siteId: string;
  templateId: string;
  templateName: string;
  templateBody: string;
  enrichmentConfigIds: string[];
  language: string;
  languageStatus: string;
  comment: string;
  type: string;
  onsiteValidationMessage?: string;
  t: TFunction;
}
export const saveTemplate = async ({
  siteId,
  templateId,
  templateName,
  templateBody,
  enrichmentConfigIds,
  language,
  languageStatus,
  comment,
  type,
  t,
}: SaveTemplateRequest): Promise<any> => {
  try {
    const payload = {
      templateName: templateName,
      comment,
      template: templateBody,
      language,
      status: languageStatus,
      enrichmentConfigIds: enrichmentConfigIds,
      type,
    };
    const result = await put<IOffsiteTemplatePreviewResponse, IUpdateTemplatePayload>(
      generatePath(TemplateApiEndpoints.UPDATE_TEMPLATE, { siteId, templateId }),
      payload,
    );
    if (result?.data === null) {
      throw Error(t('designGallery.templateEditor.queries.save.error'));
    }
    return result.data;
  } catch (e) {
    throw Error(t('designGallery.templateEditor.queries.save.error'));
  }
};

export const saveOnsiteTemplate = async ({
  siteId,
  templateId,
  templateName,
  templateBody,
  enrichmentConfigIds,
  language,
  languageStatus,
  comment,
  type,
  onsiteValidationMessage,
  t,
}: SaveTemplateRequest): Promise<any> => {
  try {
    const payload = {
      templateName: templateName,
      comment,
      template: templateBody,
      language,
      status: languageStatus,
      enrichmentConfigIds: enrichmentConfigIds,
      type,
      validationMessage: onsiteValidationMessage,
      stylesheet: '{}',
    };
    const result = await put<IOffsiteTemplatePreviewResponse, IUpdateTemplatePayload>(
      generatePath(TemplateApiEndpoints.UPDATE_ONSITE_TEMPLATE, { siteId, templateId }),
      payload,
    );
    if (result?.data === null) {
      throw Error(t('designGallery.templateEditor.queries.save.error'));
    }
    return result.data;
  } catch (e) {
    throw Error(t('designGallery.templateEditor.queries.save.error'));
  }
};

interface ConvertTemplateRequest {
  payload: IConvertTemplateRequestPayload;
  t: TFunction;
}
export const convertTemplate = async ({ payload, t }: ConvertTemplateRequest): Promise<any> => {
  try {
    const result = await post<IOffsiteTemplatePreviewResponse, IConvertTemplateRequestPayload>(
      generatePath(TemplateApiEndpoints.CONVERT),
      payload,
    );
    if (result?.data === null) {
      throw Error(t('designGallery.templateEditor.queries.save.error'));
    }
    return result.data;
  } catch (e) {
    throw Error(t('designGallery.templateEditor.queries.save.error'));
  }
};

interface TemplateVersionHistoryRequest {
  siteId: string;
  templateId: string;
  language: string;
  t: TFunction;
}

export const getTemplateVersionHistory = async ({
  siteId,
  templateId,
  language,
  t,
}: TemplateVersionHistoryRequest): Promise<ITemplateHistoryItem[]> => {
  try {
    const result = await get<any[]>(
      generatePath(TemplateApiEndpoints.GET_TEMPLATE_HISTORY, { siteId, templateId, language }),
    );
    if (result?.data === null) {
      return [];
    }
    return result.data as ITemplateHistoryItem[];
  } catch (e) {
    throw Error(t('designGallery.templateEditor.queries.templateHistory.error'));
  }
};

interface RestoreVersionRequest {
  siteId: string;
  templateId: string;
  language: string;
  versionId: string;
  t: TFunction;
}

export async function restoreTemplateVersion({ siteId, templateId, language, versionId, t }: RestoreVersionRequest) {
  try {
    const payload = {
      versionId: versionId,
    };
    const result = await post<IRestoreTemplateVersionResponse, IRestoreTemplateVersionRequestPayload>(
      generatePath(TemplateApiEndpoints.RESTORE_TEMPLATE_VERSION, {
        siteId,
        templateId,
        language,
      }),
      payload,
    );
    if (result?.data === null) {
      throw Error(t('designGallery.templateEditor.queries.restore.error'));
    }
    return result.data;
  } catch (e) {
    throw Error(t('designGallery.templateEditor.queries.restore.error'));
  }
}
